import React from "react";
import { useHistory } from "react-router-dom";
import {
  CheckCircle2
} from "lucide-react";
import SEO from "../../components/SEO/SEO";
import "./index.css";

export default function PricingLandingScreen() {
  const history = useHistory();

  return (
    <div className="pricing-landing-container min-h-screen flex flex-col">
      <SEO 
        title="Membership & Pricing - SPACIA" 
        description="Simple Access. Flexible Options. Whether you're booking for yourself, listing your space or managing bookings for a business, SPACIA gives you flexible ways to access the platform."
      />

      {/* 1. HERO BANNER (Screenshot 1) */}
      <section className="pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase block">
              MEMBERSHIP & PRICING
            </span>
            <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight leading-tight text-gray-900">
              Simple Access. <br />
              <span className="text-blue-600">Flexible Options.</span>
            </h1>
            <p className="text-gray-600 text-base sm:text-lg max-w-xl leading-relaxed">
              Whether you're booking for yourself, listing your space or managing bookings for a business, SPACIA gives you flexible ways to access the platform.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <button 
                onClick={() => history.push("/package-signup")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-full font-bold text-sm shadow-lg transition-all flex items-center gap-2"
              >
                Get Started ➔
              </button>
              <button 
                onClick={() => history.push("/pricing")}
                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-7 py-3.5 rounded-full font-bold text-sm transition-all"
              >
                Speak to Our Team
              </button>
            </div>
          </div>

          {/* Right Floating Stack Mockup */}
          <div className="lg:col-span-5 space-y-4">
            {/* Card 1: Individual */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 shadow-sm space-y-1 relative">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">INDIVIDUAL</span>
                <span className="bg-blue-600 text-white text-[9px] px-2.5 py-0.5 rounded-full font-bold">Pay-As-You-Go</span>
              </div>
              <h4 className="font-bold text-sm text-gray-900">For individuals booking Work, Stay & Play.</h4>
            </div>

            {/* Connecting Badge */}
            <div className="flex justify-center">
              <span className="bg-[#072654] text-white text-[10px] px-4 py-1 rounded-full font-bold uppercase tracking-wider shadow-sm">SPACIA</span>
            </div>

            {/* Card 2: Partner */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 shadow-sm space-y-1 relative">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">PARTNER</span>
                <span className="bg-slate-900 text-white text-[9px] px-2.5 py-0.5 rounded-full font-bold">List Your Space</span>
              </div>
              <h4 className="font-bold text-sm text-gray-900">For businesses listing spaces & services.</h4>
            </div>

            {/* Connecting Badge */}
            <div className="flex justify-center">
              <span className="bg-[#072654] text-white text-[10px] px-4 py-1 rounded-full font-bold uppercase tracking-wider shadow-sm">SPACIA</span>
            </div>

            {/* Card 3: Business & Enterprise */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 shadow-sm space-y-1 relative">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">BUSINESS & ENTERPRISE</span>
                <span className="bg-black text-white text-[9px] px-2.5 py-0.5 rounded-full font-bold">Flexible Solutions</span>
              </div>
              <h4 className="font-bold text-sm text-gray-900">For organisations managing bookings at scale.</h4>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CHOOSE THE OPTION THAT WORKS FOR YOU (Screenshot 1) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full border-t border-gray-100">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Choose the Option That Works for You</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Option 01 */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 space-y-4 hover:shadow-md transition-shadow">
            <span className="text-4xl font-extrabold text-gray-300 block">01</span>
            <h3 className="text-xl font-bold text-gray-900">Individual</h3>
            <p className="text-xs text-gray-500 leading-relaxed">For people booking Work, Stay and Play experiences.</p>
            <div className="pt-4 border-t border-gray-200/60">
              <button onClick={() => history.push("/package-signup")} className="text-blue-600 font-bold text-xs hover:text-blue-700 inline-flex items-center gap-1.5">
                Get Started ➔
              </button>
            </div>
          </div>

          {/* Option 02 */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 space-y-4 hover:shadow-md transition-shadow">
            <span className="text-4xl font-extrabold text-gray-300 block">02</span>
            <h3 className="text-xl font-bold text-gray-900">Partner</h3>
            <p className="text-xs text-gray-500 leading-relaxed">For businesses listing spaces and services on SPACIA.</p>
            <div className="pt-4 border-t border-gray-200/60">
              <button onClick={() => history.push("/host")} className="text-blue-600 font-bold text-xs hover:text-blue-700 inline-flex items-center gap-1.5">
                List Your Space ➔
              </button>
            </div>
          </div>

          {/* Option 03 */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 space-y-4 hover:shadow-md transition-shadow">
            <span className="text-4xl font-extrabold text-gray-300 block">03</span>
            <h3 className="text-xl font-bold text-gray-900">Business & Enterprise</h3>
            <p className="text-xs text-gray-500 leading-relaxed">For organisations managing bookings across teams and locations.</p>
            <div className="pt-4 border-t border-gray-200/60">
              <button onClick={() => history.push("/pricing")} className="text-blue-600 font-bold text-xs hover:text-blue-700 inline-flex items-center gap-1.5">
                Request a Quote ➔
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. INDIVIDUAL USERS SECTION (Screenshot 1 & 2) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full bg-slate-50 border-t border-b border-gray-200/80">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase">INDIVIDUAL USERS</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Individual Users</h2>
            <p className="text-blue-600 font-bold text-sm">Pay-As-You-Go</p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Book the spaces and experiences you need, when you need them. Pay for what you book — from a coworking desk for the day to a hotel room for the week.
            </p>
            <button 
              onClick={() => history.push("/package-signup")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-8 py-3.5 rounded-full shadow-md transition-all"
            >
              Get Started ➔
            </button>
          </div>

          <div className="lg:col-span-6">
            {/* Booking Flow Mockup Container */}
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-md space-y-6">
              <div className="bg-blue-600 text-white p-3 rounded-2xl flex justify-between items-center text-xs font-bold">
                <span>BOOKING FLOW</span>
                <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-[10px] uppercase">PAY-AS-YOU-GO</span>
              </div>
              
              <div className="grid grid-cols-4 gap-2 text-center text-[10px] font-bold text-gray-600">
                <div><div className="w-8 h-8 rounded-full border-2 border-blue-600 text-blue-600 flex items-center justify-center mx-auto mb-1">1</div>Find</div>
                <div><div className="w-8 h-8 rounded-full border-2 border-blue-600 text-blue-600 flex items-center justify-center mx-auto mb-1">2</div>Choose</div>
                <div><div className="w-8 h-8 rounded-full border-2 border-blue-600 text-blue-600 flex items-center justify-center mx-auto mb-1">3</div>Book</div>
                <div><div className="w-8 h-8 rounded-full border-2 border-blue-600 text-blue-600 flex items-center justify-center mx-auto mb-1">4</div>Pay</div>
              </div>

              <div className="flex gap-2">
                <span className="bg-blue-600 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">WORK</span>
                <span className="bg-blue-900 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">STAY</span>
                <span className="bg-sky-500 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">PLAY</span>
              </div>

              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-[11px] text-gray-600 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Pay for what you book — no subscription required
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. PARTNERS SECTION (Screenshot 2) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            {/* Partner Dashboard Mockup */}
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-md space-y-6">
              <div className="bg-[#072654] text-white p-3 rounded-2xl flex justify-between items-center text-xs font-bold">
                <span>PARTNER DASHBOARD</span>
                <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full text-[10px] uppercase">Live</span>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-bold text-gray-900">Your listed space</p>
                <p className="text-[10px] text-gray-400">Coworking • Nairobi</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div><p className="text-[10px] text-gray-400 uppercase font-bold">BOOKINGS</p><p className="font-bold text-sm text-gray-900">—</p></div>
                <div><p className="text-[10px] text-gray-400 uppercase font-bold">VISIBILITY</p><p className="font-bold text-sm text-emerald-600">Active</p></div>
                <div><p className="text-[10px] text-gray-400 uppercase font-bold">STATUS</p><p className="font-bold text-sm text-emerald-600">Live</p></div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center text-[9px] font-bold text-gray-500">
                <div><span className="w-5 h-5 rounded-full bg-blue-600 text-white inline-flex items-center justify-center mb-1">1</span><br/>Register</div>
                <div><span className="w-5 h-5 rounded-full bg-blue-600 text-white inline-flex items-center justify-center mb-1">2</span><br/>List</div>
                <div><span className="w-5 h-5 rounded-full bg-blue-600 text-white inline-flex items-center justify-center mb-1">3</span><br/>Manage</div>
                <div><span className="w-5 h-5 rounded-full bg-blue-600 text-white inline-flex items-center justify-center mb-1">4</span><br/>Grow</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase">PARTNERS</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Partners</h2>
            <p className="text-blue-600 font-bold text-sm">List Your Space</p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Join SPACIA and make your space or service discoverable to users looking for places to Work, Stay and Play.
            </p>
            <button 
              onClick={() => history.push("/host")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-8 py-3.5 rounded-full shadow-md transition-all"
            >
              List Your Space ➔
            </button>
          </div>
        </div>
      </section>

      {/* 5. BUILT AROUND YOUR ORGANISATION (DARK BLUE BANNER) (Screenshot 2) */}
      <section className="bg-[#072654] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-blue-400 font-bold text-xs tracking-wider uppercase block">BUSINESS & ENTERPRISE</span>
            <h2 className="text-3xl sm:text-5xl font-extrabold">Built Around Your Organisation</h2>
            <p className="text-slate-300 text-xs sm:text-sm">For organisations with broader booking requirements, SPACIA offers flexible solutions tailored to their needs.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 space-y-2">
              <span className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center text-sm mb-3">👥</span>
              <h4 className="font-bold text-base">Teams</h4>
              <p className="text-xs text-slate-300 leading-relaxed">Flexible access for teams of any size.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 space-y-2">
              <span className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center text-sm mb-3">📅</span>
              <h4 className="font-bold text-base">Multiple Bookings</h4>
              <p className="text-xs text-slate-300 leading-relaxed">Manage multiple simultaneous bookings.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 space-y-2">
              <span className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center text-sm mb-3">📍</span>
              <h4 className="font-bold text-base">Multiple Locations</h4>
              <p className="text-xs text-slate-300 leading-relaxed">Book across all your operating cities.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 space-y-2">
              <span className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center text-sm mb-3">📊</span>
              <h4 className="font-bold text-base">Centralised Management</h4>
              <p className="text-xs text-slate-300 leading-relaxed">One dashboard for your whole organisation.</p>
            </div>
          </div>

          <div className="flex justify-center gap-4 pt-4">
            <button onClick={() => history.push("/pricing")} className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs px-8 py-3.5 rounded-full shadow-lg">
              Request a Quote ➔
            </button>
            <button onClick={() => history.push("/pricing")} className="bg-transparent hover:bg-white/10 border border-white/30 text-white font-bold text-xs px-8 py-3.5 rounded-full">
              Speak to Our Team
            </button>
          </div>
        </div>
      </section>

      {/* 6. HOW SPACIA WORKS FOR YOU (Screenshot 2 & 3) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">How SPACIA Works for You</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 space-y-4">
            <span className="text-3xl font-extrabold text-gray-300 block">01</span>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">INDIVIDUAL</span>
            <h4 className="font-bold text-lg text-gray-900">Pay-As-You-Go</h4>
            <p className="text-xs text-gray-500">For individual users.</p>
            <button onClick={() => history.push("/package-signup")} className="w-full bg-white border border-gray-200 text-gray-700 font-bold text-xs py-3 rounded-xl mt-4">
              Get Started ➔
            </button>
          </div>

          <div className="bg-white border-2 border-blue-600 rounded-3xl p-8 space-y-4 shadow-lg">
            <span className="text-3xl font-extrabold text-blue-600 block">02</span>
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider block">PARTNER</span>
            <h4 className="font-bold text-lg text-gray-900">List Your Space</h4>
            <p className="text-xs text-gray-500">For spaces and service providers.</p>
            <button onClick={() => history.push("/host")} className="w-full bg-blue-600 text-white font-bold text-xs py-3 rounded-xl mt-4">
              List Your Space ➔
            </button>
          </div>

          <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 space-y-4">
            <span className="text-3xl font-extrabold text-gray-300 block">03</span>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">BUSINESS & ENTERPRISE</span>
            <h4 className="font-bold text-lg text-gray-900">Flexible Solutions</h4>
            <p className="text-xs text-gray-500">For organisations and teams.</p>
            <button onClick={() => history.push("/pricing")} className="w-full bg-white border border-gray-200 text-gray-700 font-bold text-xs py-3 rounded-xl mt-4">
              Request a Quote ➔
            </button>
          </div>
        </div>
      </section>

      {/* 7. ONE PLATFORM. MULTIPLE EXPERIENCES (Screenshot 3) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full bg-slate-50 border-t border-b border-gray-200/80">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900">One Platform. Multiple Experiences.</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* WORK */}
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-blue-600 text-white font-bold text-xs py-3 text-center uppercase tracking-wider">WORK</div>
            <ul className="p-6 space-y-2 text-xs text-gray-700 font-medium">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Coworking Spaces</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Dedicated Desks</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Meeting Rooms</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Conference Rooms</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Private Offices</li>
            </ul>
          </div>

          {/* STAY */}
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-[#072654] text-white font-bold text-xs py-3 text-center uppercase tracking-wider">STAY</div>
            <ul className="p-6 space-y-2 text-xs text-gray-700 font-medium">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Hotels</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Apartments</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Boutique Hotels</li>
            </ul>
          </div>

          {/* PLAY */}
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-sky-500 text-white font-bold text-xs py-3 text-center uppercase tracking-wider">PLAY</div>
            <ul className="p-6 space-y-2 text-xs text-gray-700 font-medium">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Wellness</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Dining</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Adventure</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Events</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Concierge</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 8. FROM ONE BOOKING TO MULTIPLE LOCATIONS & FLEXIBLE ACCESS FOR DIFFERENT NEEDS (Screenshot 3 & 4) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full space-y-24">
        {/* From One Booking to Multiple Locations */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">From One Booking to Multiple Locations</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 space-y-4 text-center">
              <span className="bg-blue-600 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">INDIVIDUAL</span>
              <div className="space-y-2 pt-2 text-xs font-semibold text-gray-700">
                <div className="bg-white p-3 rounded-xl border border-gray-200">👤 One Person</div>
                <div className="bg-white p-3 rounded-xl border border-gray-200">📅 One Booking</div>
                <div className="bg-white p-3 rounded-xl border border-gray-200">✨ One Experience</div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 space-y-4 text-center">
              <span className="bg-[#072654] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">BUSINESS</span>
              <div className="space-y-2 pt-2 text-xs font-semibold text-gray-700">
                <div className="bg-white p-3 rounded-xl border border-gray-200">👥 Teams</div>
                <div className="bg-white p-3 rounded-xl border border-gray-200">📅 Multiple Bookings</div>
                <div className="bg-white p-3 rounded-xl border border-gray-200">📍 Multiple Locations</div>
              </div>
            </div>
          </div>
        </div>

        {/* Flexible Access for Different Needs */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">Flexible Access for Different Needs</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 font-bold flex items-center justify-center mx-auto text-xl">📅</div>
              <h3 className="font-extrabold text-xl text-blue-600">BOOK</h3>
              <p className="text-xs text-gray-500">For individual users.</p>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 font-bold flex items-center justify-center mx-auto text-xl">🏢</div>
              <h3 className="font-extrabold text-xl text-blue-600">LIST</h3>
              <p className="text-xs text-gray-500">For Partners.</p>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 font-bold flex items-center justify-center mx-auto text-xl">⚙️</div>
              <h3 className="font-extrabold text-xl text-blue-600">MANAGE</h3>
              <p className="text-xs text-gray-500">For businesses and teams.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. QUESTIONS ABOUT PRICING? (Screenshot 4) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full text-center">
        <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-10 space-y-4">
          <h2 className="text-3xl font-extrabold text-gray-900">Questions About Pricing?</h2>
          <p className="text-xs text-gray-500 max-w-md mx-auto leading-relaxed">
            Find answers about booking, payments, Partners and Business & Enterprise.
          </p>
          <button onClick={() => history.push("/pricing")} className="bg-white border border-blue-600 text-blue-600 font-bold text-xs px-6 py-2.5 rounded-full hover:bg-blue-50 transition-all">
            View FAQs ➔
          </button>
        </div>
      </section>

      {/* 10. READY TO DISCOVER SPACIA? DARK BLUE CTA BANNER (Screenshot 4) */}
      <section className="bg-[#072654] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl sm:text-5xl font-extrabold">Ready to Discover SPACIA?</h2>
          <p className="text-slate-300 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
            Wherever you need to Work, Stay or Play, SPACIA gives you one place to find it, book it and manage it with confidence.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <button onClick={() => history.push("/package-signup")} className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs px-8 py-3.5 rounded-full shadow-lg transition-all">
              Get Started ➔
            </button>
            <button onClick={() => history.push("/host")} className="bg-transparent border border-white/40 text-white hover:bg-white/10 font-bold text-xs px-8 py-3.5 rounded-full transition-all">
              List Your Space
            </button>
            <button onClick={() => history.push("/pricing")} className="bg-transparent text-slate-300 hover:text-white font-bold text-xs px-6 py-3.5">
              Speak to Our Team
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
